import { translate } from '@aleph/shared-components';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { User, UserAccountLevel } from 'wb-spotify/graphql/authgraph/generated';
import type { GraphQLErrorWithFormValidation } from 'wb-spotify/app/routes/superGrid/utils';

export const REFRESH_TOKEN_URI = `${import.meta.env.VITE_API_PATH}/auth/refresh-token/gn_ga/user/`;

export const NETWORK = 'pn_sp';

export const getPublicClient = ({ uri }: { uri: string }) => new ApolloClient({
	uri,
	cache: new InMemoryCache(),
});

export const getUserInitials = (
	user?: any | null,
) => (user ? `${user.userName?.[0] ?? ''}${user.userSurname?.[0] ?? ''}` : '');

export enum SpotifyReportSchedule {
	OnlyOnce = 'only_once',
	EveryMonday = 'every_monday',
	EveryWeekday = 'every_weekday',
	FirstOfTheMonth = 'first_of_the_month',
}

export const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const getUserAdAccountAccess = (user: User): string[] => user?.userGroupAccess
	?.flatMap(item => item?.userGroup?.advertiserAccountAccess)
	?.filter((adAcc, index, self) => adAcc && self?.findIndex(a => a?.advertiserAccount?.advertiserAccountId === adAcc?.advertiserAccount?.advertiserAccountId) === index)
	?.map(adAcc => adAcc?.advertiserAccount?.advertiserAccountName)
	?.filter((item): item is string => item !== undefined) ?? [];

// Helper to get the type of the 'key' field from 'T'
type Field<T, Key extends keyof NonNullable<T>> = NonNullable<T>[Key];

export type RecursiveNonNullableExtractor<T, KeyPath extends (keyof any)[]> = {
	0: NonNullable<T>,
	1: KeyPath extends [infer Key, ...infer Rest]
		? Key extends keyof NonNullable<T>
			// @ts-ignore: Its fine :D
			? RecursiveNonNullableExtractor<Field<T, Key>, Rest>
			: never
		: never
}[KeyPath extends [any, ...any[]] ? 1 : 0];

export const SPOTIFY_ADMIN_ROLES = [
	UserAccountLevel.PlatformAdmin,
	UserAccountLevel.AgencyAdmin,
	UserAccountLevel.AdvertiserAdmin,
];

export const SPOTIFY_ADVERTISERS_ROUTE_ALLOWED_ROLES = SPOTIFY_ADMIN_ROLES;

/**
 * Checks if two arrays contain the same elements, ignoring order.
 */
export function arraysHaveSameElements<T>(arr1: T[], arr2: T[]) {
	if (arr1.length !== arr2.length) return false;
	const set2 = new Set(arr2);

	return arr1.every(s => set2.has(s));
}

export function isGraphQLErrorWithFormValidation(error: unknown): error is GraphQLErrorWithFormValidation {
	if (!error || typeof error !== 'object' || Array.isArray(error)) {
		return false;
	}

	const err = error as Record<string, unknown>;

	if (err.form !== undefined && (typeof err.form !== 'object' || err.form === null || Array.isArray(err.form))) {
		return false;
	}

	if (err.form) {
		const form = err.form as Record<string, unknown>;

		return Object.values(form).every(
			(value) => {
				if (value === null) return true;
				if (typeof value !== 'object' || Array.isArray(value)) return false;

				const formValue = value as Record<string, unknown>;

				return formValue.message === undefined || typeof formValue.message === 'string';
			},
		);
	}

	return true;
}

/**
 * Format the GraphQLErrorWithFormValidation errors returned from BE as a single string
 */
export function getGraphqlFormErrors(errors: unknown[]): string | null {
	const graphqlFormErrors = errors.filter(isGraphQLErrorWithFormValidation);

	if (graphqlFormErrors.length === 0) {
		return null;
	}

	const formattedErrors = graphqlFormErrors
		.map((e) => {
			const message = e.message ?? '';
			const formMessages = e.form
				? Object.keys(e.form)
					.map((key) => {
						const formField = e.form?.[key];

						return (
							formField?.message
								? translate(formField.message, { defaultValue: 'sc: unknown err' })
								: ''
						);
					})
					.filter(Boolean)
					.join(', ')
				: '';

			return formMessages ? `${message} - ${formMessages}` : message;
		})
		.filter(Boolean);

	if (formattedErrors.length === 0) {
		return null;
	}

	return formattedErrors.join(', ');
}
